import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Box,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import moment from 'moment';
import { func, string } from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';

const PRESETS = [
  { id: 'd7', labelKey: 'HrBot.DeliveryOptions.expiresPreset.d7', addDays: 7 },
  { id: 'd14', labelKey: 'HrBot.DeliveryOptions.expiresPreset.d14', addDays: 14 },
  { id: 'd30', labelKey: 'HrBot.DeliveryOptions.expiresPreset.d30', addDays: 30 },
  { id: 'd60', labelKey: 'HrBot.DeliveryOptions.expiresPreset.d60', addDays: 60 },
  { id: 'never', labelKey: 'HrBot.DeliveryOptions.expiresPreset.never' },
];

const today = () => moment().startOf('day');

const inferPreset = (preset, expiresAt) => {
  if (preset && PRESETS.some(p => p.id === preset)) return preset;
  if (preset === 'custom') return 'custom';
  if (expiresAt === '' || expiresAt == null) return 'never';
  const m = moment(expiresAt, 'YYYY-MM-DD', true);
  if (!m.isValid()) return 'custom';
  for (const p of PRESETS) {
    if (p.addDays != null && m.isSame(today().add(p.addDays, 'days'), 'day')) {
      return p.id;
    }
  }
  return 'custom';
};

const ExpirationPicker = ({ preset: presetProp = '', expiresAt = '', onChange }) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const preset = useMemo(() => inferPreset(presetProp, expiresAt), [presetProp, expiresAt]);

  const triggerLabel = useMemo(() => {
    if (preset === 'never') {
      return I18n.t('HrBot.DeliveryOptions.expiresPreset.never');
    }
    const fixed = PRESETS.find(p => p.id === preset);
    if (fixed) return I18n.t(fixed.labelKey);
    if (expiresAt && String(expiresAt).trim()) {
      return moment(expiresAt).format('L');
    }
    return '';
  }, [preset, expiresAt]);

  const handlePresetClick = useCallback(
    presetId => {
      const p = PRESETS.find(item => item.id === presetId);
      if (!p) return;
      if (p.id === 'never') {
        onChange({ preset: 'never', expiresAt: '' });
      } else {
        onChange({
          preset: p.id,
          expiresAt: today().add(p.addDays, 'days').format('YYYY-MM-DD'),
        });
      }
      setOpen(false);
    },
    [onChange],
  );

  const handleCalendarChange = useCallback(
    momentDate => {
      if (!momentDate?.isValid?.()) return;
      onChange({
        preset: 'custom',
        expiresAt: momentDate.format('YYYY-MM-DD'),
      });
      setOpen(false);
    },
    [onChange],
  );

  const calendarValue = useMemo(() => {
    if (preset === 'never') return null;
    if (expiresAt && String(expiresAt).trim()) {
      const m = moment(expiresAt, 'YYYY-MM-DD', true);
      return m.isValid() ? m : null;
    }
    return null;
  }, [preset, expiresAt]);

  return (
    <>
      <TextField
        size="small"
        ref={triggerRef}
        fullWidth
        variant="outlined"
        sx={{
          cursor: 'pointer',
          '& input': {
            cursor: 'pointer',
            height: '1.1876em',
          },
        }}
        value={triggerLabel}
        onClick={() => setOpen(true)}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayIcon
                  sx={{ fontSize: 18, color: open ? 'primary.main' : 'text.secondary' }}
                />
              </InputAdornment>
            ),
          },
          htmlInput: {
            'aria-label': I18n.t('HrBot.DeliveryOptions.expiresAt'),
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              overflow: 'hidden',
              borderRadius: 1,
            },
          },
        }}
        sx={{ mt: 1 }}
      >
        <List
          disablePadding
          sx={theme => ({
            width: 160,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.grey[50],
            p: 0.5,
          })}
        >
          {PRESETS.map(p => (
            <ListItemButton
              key={p.id}
              selected={preset === p.id}
              onClick={() => handlePresetClick(p.id)}
              dense
              sx={theme => ({
                borderRadius: 1,
                py: 0.75,
                px: 1.5,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                },
              })}
            >
              <ListItemText
                sx={{
                  m: 0,
                  '& p': {
                    color: 'inherit',
                  },
                }}
                primary={I18n.t(p.labelKey)}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ p: 1 }}>
          <DateCalendar value={calendarValue} onChange={handleCalendarChange} minDate={today()} />
        </Box>
      </Popover>
    </>
  );
};

ExpirationPicker.propTypes = {
  preset: string,
  expiresAt: string,
  onChange: func.isRequired,
};

export default ExpirationPicker;
